/**
 * conwayLifePipeline — Conway's Game of Life as a real CA.
 *
 * Real ping-pong texture simulation:
 *   - A 256x256 RGBA8 texture holds cell state (R = alive/dead, GBA = age/hue).
 *   - Each frame, run 12 update steps via a compute shader.
 *   - The render pass reads the texture and visualizes it.
 *
 * The classic rules: a dead cell with 3 live neighbors becomes alive
 * (reproduction); a live cell with 2 or 3 live neighbors stays alive
 * (survival); otherwise it dies.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ConwayLifePipeline {
  computeProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  computeVAO: WebGLVertexArrayObject | null;
  renderVAO: WebGLVertexArrayObject | null;
  quadBuffer: WebGLBuffer | null;
  cellTextures: [WebGLTexture | null, WebGLTexture | null];
  framebuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
  cellSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const COMPUTE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform float uTime;
uniform float uSeed;

// Helper to sample with wrap.
vec4 sampleCell(vec2 uv) {
  return texture(uPrevState, uv);
}

void main() {
  vec2 texel = vec2(1.0) / vec2(textureSize(uPrevState, 0));
  vec4 me = sampleCell(vUV);
  // Count live neighbors.
  float n = 0.0;
  float sumHue = 0.0;
  for (int dx = -1; dx <= 1; dx++) {
    for (int dy = -1; dy <= 1; dy++) {
      if (dx == 0 && dy == 0) continue;
      vec4 c = sampleCell(vUV + vec2(float(dx), float(dy)) * texel);
      if (c.r > 0.5) {
        n += 1.0;
        sumHue += c.g;
      }
    }
  }
  bool alive = me.r > 0.5;
  float age = me.b;
  bool nextAlive;
  if (alive) {
    nextAlive = (n >= 1.9 && n <= 3.1);  // 2 or 3 neighbors
  } else {
    nextAlive = (n >= 2.9 && n <= 3.1);  // exactly 3 neighbors
  }
  if (nextAlive) {
    if (alive) {
      // Surviving cell — age up.
      outState = vec4(1.0, me.g, min(me.b + 0.02, 1.0), me.a);
    } else {
      // Born cell — pick a hue based on neighbor average.
      float newHue = sumHue / max(n, 1.0);
      outState = vec4(1.0, newHue, 0.0, 1.0);
    }
  } else {
    outState = vec4(0.0, me.g, 0.0, 1.0);
  }
}
`;

const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  vec4 me = texture(uState, vUV);
  float alive = step(0.5, me.r);
  float age = me.g;
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  // Density from 5x5 neighborhood.
  float density = 0.0;
  for (int dy = -2; dy <= 2; dy++) {
    for (int dx = -2; dx <= 2; dx++) {
      vec2 uv = vUV + vec2(float(dx), float(dy)) * texel * 2.0;
      density += step(0.5, texture(uState, uv).r);
    }
  }
  density /= 25.0;
  vec3 bgA = vec3(0.01, 0.04, 0.08);
  vec3 bgB = vec3(0.02, 0.08, 0.12);
  vec3 col = mix(bgA, bgB, density * 0.5);
  if (alive > 0.5) {
    vec3 youngColor = vec3(0.85, 0.95, 1.0);
    vec3 oldColor = vec3(0.95, 0.65, 0.20);
    vec3 cellColor = mix(youngColor, oldColor, smoothstep(0.0, 1.0, age));
    col += cellColor * (0.5 + density * 1.5);
  }
  vec2 gridLine = abs(fract(vUV * 80.0) - 0.5);
  float grid = 1.0 - smoothstep(0.40, 0.50, max(gridLine.x, gridLine.y));
  col = mix(col, col * 0.5, grid * 0.5);
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  col *= 1.0 + bassAvg * 0.4;
  outColor = vec4(col, 1.0);
}
`;

function createCellTexture(gl: WebGL2RenderingContext, size: number, seed: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  // Initial state: random cells.
  const pixels = new Uint8Array(size * size * 4);
  // Mulberry32 PRNG.
  let s = seed | 0;
  function rand() {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  for (let i = 0; i < size * size; i++) {
    const r = rand();
    const alive = r < 0.30 ? 255 : 0;
    const hue = rand();
    pixels[i * 4 + 0] = alive;
    pixels[i * 4 + 1] = Math.floor(hue * 255);
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 255;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachToFramebuffer(
  gl: WebGL2RenderingContext,
  tex: WebGLTexture,
  size: number,
): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createConwayLifePipeline(gl: WebGL2RenderingContext, size = 128): ConwayLifePipeline | null {
  const computeProgram = linkProgram(gl, VERT_SRC, COMPUTE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!computeProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  // Initial seed for reproducibility.
  const tex0 = createCellTexture(gl, size, 12345);
  const tex1 = createCellTexture(gl, size, 67890);
  const fb0 = attachToFramebuffer(gl, tex0, size);
  const fb1 = attachToFramebuffer(gl, tex1, size);

  return {
    computeProgram,
    renderProgram,
    computeVAO: vao,
    renderVAO: vao,
    quadBuffer: vbo,
    cellTextures: [tex0, tex1],
    framebuffers: [fb0, fb1],
    pingIdx: 0,
    uTime: gl.getUniformLocation(computeProgram, "uTime"),
    uBass: gl.getUniformLocation(computeProgram, "uBass"),
    uMid: gl.getUniformLocation(computeProgram, "uMid"),
    uHigh: gl.getUniformLocation(computeProgram, "uHigh"),
    bands: new Float32Array(9),
    cellSize: size,
  };
}

export function computeConwayLifeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeConwayLifeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawConwayLife(
  gl: WebGL2RenderingContext,
  pipe: ConwayLifePipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.computeProgram || !pipe.renderProgram) return;
  const size = pipe.cellSize;
  // Run a few update steps.
  const STEPS_PER_FRAME = 2;
  for (let step = 0; step < STEPS_PER_FRAME; step++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.cellTextures[srcIdx];
    const dstFb = pipe.framebuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.computeProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.computeProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    if (pipe.uTime) gl.uniform1f(pipe.uTime, t);
    if (pipe.uBass) gl.uniform3f(pipe.uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
    if (pipe.uMid) gl.uniform3f(pipe.uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
    if (pipe.uHigh) gl.uniform3f(pipe.uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
    gl.bindVertexArray(pipe.computeVAO);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    pipe.pingIdx = dstIdx;
  }

  // Render to the screen.
  const finalTex = pipe.cellTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  const uCellState = gl.getUniformLocation(pipe.renderProgram, "uCellState");
  if (uCellState) gl.uniform1i(uCellState, 0);
  const uTimeRender = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTimeRender) gl.uniform1f(uTimeRender, t);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  gl.bindVertexArray(pipe.renderVAO);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}
